import React, { useEffect } from 'react';
import ApexCharts from 'apexcharts';
import useDashboardStore from '../../../store/builders/useDashboardStore';
import { useNavigate } from 'react-router';

const ProjectTile = ({ number, title, icon, chartId, chartColor, lineWidth }) => {
  const { chartData, getChartData } = useDashboardStore();
  const navigate = useNavigate();

  const handleclick = () =>{
    switch(title.toLowerCase()){
      case 'live projects':
        navigate('/builder/liveproject');
        break;
      case 'pending projects':
        navigate('/builder/pendingproject');
        break;
      case 'completed projects':
        navigate('/builder/completedproject');
        break;
      case 'project enquiries':
        navigate('/builder/projectenquiry');
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    getChartData();
  }, [getChartData]);

  useEffect(() => {
    if (!chartData || !chartData.labels || !chartData.datasets) return;

    // Find the dataset that matches the title
    const datasetName = title.replace(' projects', ' Projects').replace(' enquiries', ' Enquiries');
    const selectedDataset = chartData.datasets.find(dataset => dataset.name === datasetName);
    
    if (!selectedDataset) return;

    const options = {
      chart: {
        height: 80,
        width: 100,
        type: "area",
        fontFamily: "Inter, sans-serif",
        dropShadow: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
      },
      tooltip: {
        enabled: true,
        followCursor: true,
        custom: function({ series, seriesIndex, dataPointIndex }) {
          const value = series[seriesIndex][dataPointIndex];
          return `<div class="custom-tooltip">
            <span class="font-semibold">
              <span style="color: ${chartColor}">●</span> ${title}
            </span>
            <span class="text-sm">${value}</span>
          </div>`;
        },
        fixed: {
          enabled: false,
        },
        marker: {
          show: false,
        },
        x: {
          show: false,
        },
        y: {
          title: {
            formatter: () => ''
          }
        },
        style: {
          fontSize: '12px',
          fontFamily: 'Inter, sans-serif',
        },
        onDatasetHover: {
          highlightDataSeries: false,
        },
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          }
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          opacityFrom: 0.55,
          opacityTo: 0,
          shade: chartColor,
          gradientToColors: [chartColor],
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: lineWidth,
        colors: [chartColor],
      },
      grid: {
        show: false,
        padding: {
          left: 0,
          right: 0,
          top: -20,
          bottom: -8,
        },
      },
      series: [
        {
          name: title,
          data: selectedDataset.data.slice(-6),
          color: chartColor,
        },
      ],
      xaxis: {
        categories: chartData.labels.slice(-6).map(label => {
          const [year, month] = label.split('-');
          return `${month}/${year}`;
        }),
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        show: false,
      },
    };

    // Add custom tooltip styles
    const style = document.createElement('style');
    style.textContent = `
      .custom-tooltip {
        background: #fff;
        border-radius: 4px;
        padding: 4px 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        font-size: 11px;
        display: flex;
        flex-direction: column;
        gap: 2px;
      }
    `;
    document.head.appendChild(style);

    const chart = new ApexCharts(document.getElementById(chartId), options);
    chart.render();

    return () => {
      chart.destroy();
      document.head.removeChild(style);
    };
  }, [chartId, chartColor, lineWidth, chartData, title]);

  return (
    <div 
    onClick={handleclick}
      className="bg-white h-28 w-auto rounded-xl p-3 flex flex-col justify-between relative overflow-visible cursor-pointer transform transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
      style={{
        boxShadow: "rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px",
        transition: "all 0.3s ease-in-out"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "rgba(50, 50, 93, 0.35) 0px 30px 60px -12px, rgba(0, 0, 0, 0.4) 0px 18px 36px -18px";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px";
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.boxShadow = "rgba(50, 50, 93, 0.15) 0px 6px 12px -2px, rgba(0, 0, 0, 0.2) 0px 3px 7px -3px";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.boxShadow = "rgba(50, 50, 93, 0.25) 0px 13px 27px -5px, rgba(0, 0, 0, 0.3) 0px 8px 16px -8px";
      }}
    >
      <div>
        <p className="text-xl font-bold">{number}</p>
        <p className="text-xs text-gray-600 font-semibold">{title}</p>
      </div>
      <div className="absolute bottom-3 left-3">
        <div className="rounded-full">
          <img src={icon} alt={title} className="h-8 w-8" />
        </div>
      </div>
      <div className="absolute -bottom-5 right-3 z-10">
        <div id={chartId} className="rounded-full"></div>
      </div>
    </div>
  );
};
const ProjectTiles = () => {
  const { projectTile, getProjectTile } = useDashboardStore();

  useEffect(() => {
    getProjectTile();
  }, [getProjectTile]);

  const tilesData = [
    { number: projectTile?.live_projects || 0, title: 'Live projects', icon: '/protile/engineer_10727828.png', chartId: 'chart-1', chartColor: '#2FB2AB', lineWidth: 1 },
    { number: projectTile?.pending_projects || 0, title: 'Pending Projects', icon: '/protile/onboarding_16751381.png', chartId: 'chart-2', chartColor: '#27CFA7', lineWidth: 1 },
    { number: projectTile?.completed_projects || 0, title: 'Completed projects', icon: '/protile/folder_11171774.png', chartId: 'chart-3', chartColor: '#6142FF', lineWidth: 1 },
    { number: projectTile?.project_enquiries || 0, title: 'Project enquiries', icon: '/protile/questionnaire_11275251.png', chartId: 'chart-4', chartColor: '#FA902F', lineWidth: 1 },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {tilesData.map((tile, index) => (
        <ProjectTile
          key={index}
          number={tile.number}
          title={tile.title}
          icon={tile.icon}
          chartId={tile.chartId}
          chartColor={tile.chartColor}
          lineWidth={tile.lineWidth}
        />
      ))}
    </div>
  );
};

export default ProjectTiles;